import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { userApi } from '../services/userApi';
import Avatar from './Avatar';
import './MentionInput.css';

const MentionInput = ({
  value,
  onChange,
  onKeyDown,
  placeholder,
  rows = 1,
  maxLength = 5000,
  disabled = false,
  className = '',
  inputRef = null,
}) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [mentionStartIndex, setMentionStartIndex] = useState(-1);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const wrapperRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Search users when query changes
  const searchUsers = useCallback(async (query) => {
    if (!query || query.length < 1) {
      setSuggestions([]);
      return;
    }

    try {
      const response = await userApi.searchFriends(query, 10, 0);
      if (response.success && response.data) {
        setSuggestions(response.data);
        setSelectedIndex(0);
      }
    } catch (error) {
      console.error('Error searching users:', error);
    }
  }, []);

  // Handle text changes
  const handleChange = (e) => {
    const text = e.target.value;
    const cursorPos = e.target.selectionStart;

    console.log('MentionInput handleChange:', { text, cursorPos });

    // Find if cursor is after an @ symbol
    const textBeforeCursor = text.slice(0, cursorPos);
    const atIndex = textBeforeCursor.lastIndexOf('@');

    console.log('MentionInput atIndex:', atIndex, 'textBeforeCursor:', textBeforeCursor);

    if (atIndex !== -1) {
      // Check if there's a space after @ (meaning mention is complete)
      const textAfterAt = textBeforeCursor.slice(atIndex + 1);
      const hasSpaceAfter = textAfterAt.includes(' ');

      console.log('textAfterAt:', textAfterAt, 'hasSpaceAfter:', hasSpaceAfter);

      if (!hasSpaceAfter && (atIndex === 0 || textBeforeCursor[atIndex - 1] === ' ' || textBeforeCursor[atIndex - 1] === '\n')) {
        // Start of a mention
        console.log('Starting mention search for:', textAfterAt);
        setMentionStartIndex(atIndex);
        setSearchQuery(textAfterAt);
        setShowSuggestions(true);
        searchUsers(textAfterAt);
      } else {
        console.log('Not starting mention - conditions not met');
        setShowSuggestions(false);
        setSuggestions([]);
        setMentionStartIndex(-1);
      }
    } else {
      console.log('No @ found');
      setShowSuggestions(false);
      setSuggestions([]);
      setMentionStartIndex(-1);
    }

    onChange(e);
  };

  // Handle key navigation in suggestions
  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) {
      if (onKeyDown) onKeyDown(e);
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => {
          const newIndex = (prev + 1) % suggestions.length;
          scrollToSelected(newIndex);
          return newIndex;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => {
          const newIndex = (prev - 1 + suggestions.length) % suggestions.length;
          scrollToSelected(newIndex);
          return newIndex;
        });
        break;
      case 'Enter':
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          e.preventDefault();
          selectUser(suggestions[selectedIndex]);
        }
        break;
      case 'Tab':
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          e.preventDefault();
          selectUser(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        break;
      default:
        if (onKeyDown) onKeyDown(e);
    }
  };

  // Scroll the selected item into view
  const scrollToSelected = (index) => {
    setTimeout(() => {
      const container = wrapperRef.current?.querySelector('.mention-suggestions');
      const selected = container?.querySelectorAll('.mention-suggestion')[index];
      if (selected) {
        selected.scrollIntoView({ block: 'nearest' });
      }
    }, 0);
  };

  // Select a user from suggestions
  const selectUser = (user) => {
    const text = value;
    const beforeMention = text.slice(0, mentionStartIndex);
    const afterCursor = text.slice(inputRef?.current?.selectionStart || value.length);

    // Insert @username followed by a space
    const newText = `${beforeMention}@${user.username} ${afterCursor}`;
    const newCursorPos = newText.length;

    // Create a synthetic event for onChange with all needed properties
    const event = {
      target: {
        value: newText,
        selectionStart: newCursorPos,
        selectionEnd: newCursorPos,
      }
    };
    onChange(event);

    setShowSuggestions(false);
    setSuggestions([]);
    setMentionStartIndex(-1);

    // Focus back on textarea and set cursor position
    if (inputRef?.current) {
      inputRef.current.focus();
      // Set cursor position after the username
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
        }
      }, 0);
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search when query changes
  useEffect(() => {
    if (searchQuery) {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      searchTimeoutRef.current = setTimeout(() => {
        searchUsers(searchQuery);
      }, 200);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, searchUsers]);

  // Calculate dropdown position when showing suggestions
  useEffect(() => {
    if (showSuggestions && inputRef?.current) {
      const textarea = inputRef.current;
      const rect = textarea.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
      });
    }
  }, [showSuggestions, inputRef]);

  return (
    <div className="mention-input-wrapper" ref={wrapperRef}>
      <textarea
        ref={inputRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        rows={rows}
        maxLength={maxLength}
        disabled={disabled}
        className={className}
      />

      {showSuggestions && suggestions.length > 0 && (
        <div
          className="mention-suggestions"
          style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
        >
          {suggestions.map((user, index) => (
            <div
              key={user.id}
              className={`mention-suggestion ${index === selectedIndex ? 'selected' : ''}`}
              onClick={() => selectUser(user)}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <Avatar user={user} size="small" />
              <div className="mention-suggestion-info">
                <span className="mention-suggestion-name">{user.display_name}</span>
                <span className="mention-suggestion-username">@{user.username}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MentionInput;
